import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  X, Save, Building2, Users, Boxes, Ticket, FolderArchive, FolderTree, Map,
  ShieldCheck, Pencil, Loader2, AlertTriangle, BadgeCheck,
  Power, IndianRupee, Trash2, Mail, KeyRound,
} from 'lucide-react';

const PLANS = [
  { v: 'starter',      l: 'Starter',      hint: 'Up to 25 employees · 50 GB storage' },
  { v: 'professional', l: 'Professional', hint: 'Up to 250 employees · 250 GB · advanced reports' },
  { v: 'enterprise',   l: 'Enterprise',   hint: 'Unlimited · 500+ GB · SSO + audit export + SLAs' },
];

const TIMEZONES = [
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Dubai', 'Europe/London',
  'America/New_York', 'America/Los_Angeles', 'Australia/Sydney',
];

export default function TenantDetailDrawer({ tenantId, open, onClose, onChanged }) {
  const [tab, setTab] = useState('overview'); // overview | config | users | branches | audit
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    if (!open || !tenantId) return;
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        const [tRes, sRes] = await Promise.all([
          api.get(`/super-admin/tenants/${tenantId}`),
          api.get(`/super-admin/tenants/${tenantId}/stats`),
        ]);
        if (abort) return;
        setTenant(tRes.data);
        setStats(sRes.data);
        setEdit({
          company_name: tRes.data.company_name || '',
          company_domain: tRes.data.company_domain || '',
          company_type: tRes.data.company_type || '',
          industry: tRes.data.industry || '',
          company_address: tRes.data.company_address || '',
          time_zone: tRes.data.time_zone || 'Asia/Kolkata',
          subscription_plan: tRes.data.subscription_plan || 'starter',
          storage_allocation_gb: tRes.data.storage_allocation_gb ?? 50,
        });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load tenant'); }
      finally { if (!abort) setLoading(false); }
    })();
    return () => { abort = true; };
  }, [open, tenantId]);

  const loadTab = async (next) => {
    setTab(next);
    if (!tenant) return;
    try {
      if (next === 'users' && users.length === 0) {
        const { data } = await api.get(`/super-admin/tenants/${tenantId}/users`);
        setUsers(data);
      } else if (next === 'branches' && branches.length === 0) {
        const { data } = await api.get(`/super-admin/tenants/${tenantId}/branches`);
        setBranches(data);
      } else if (next === 'audit' && audit.length === 0) {
        const { data } = await api.get(`/super-admin/tenants/${tenantId}/audit?limit=50`);
        setAudit(data);
      }
    } catch (e) { /* tab-level errors are non-fatal */ }
  };

  const save = async () => {
    if (!edit?.company_name?.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}`, {
        ...edit,
        storage_allocation_gb: parseInt(edit.storage_allocation_gb, 10) || 0,
      });
      toast.success('Tenant configuration saved');
      const { data } = await api.get(`/super-admin/tenants/${tenantId}`);
      setTenant(data);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleSubscription = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/super-admin/tenants/${tenantId}/subscription/toggle`);
      toast.success(`Subscription ${data.subscription_active ? 'resumed' : 'paused'}`);
      setTenant((t) => ({ ...t, subscription_active: data.subscription_active }));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleBilling = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/super-admin/tenants/${tenantId}/billing/toggle`);
      toast.success(`Billing ${data.billing_enabled ? 'enabled' : 'disabled'} for this tenant`);
      setTenant((t) => ({ ...t, billing_enabled: data.billing_enabled }));
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const hardDelete = async () => {
    const confirm = window.prompt(`This will PERMANENTLY delete '${tenant?.company_name}' and ALL its data (users, tickets, assets, branches, departments, vault files, plans).\n\nType the tenant ID '${tenantId}' to confirm:`);
    if (confirm !== tenantId) { if (confirm != null) toast.error('Confirmation did not match.'); return; }
    setSaving(true);
    try {
      await api.delete(`/super-admin/tenants/${tenantId}/hard?confirm=${encodeURIComponent(tenantId)}`);
      toast.success('Tenant permanently deleted.');
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [busyUserId, setBusyUserId] = useState(null);
  const [resendCreds, setResendCreds] = useState(null);

  const resendWelcome = async (u) => {
    if (!window.confirm(`Generate a fresh temporary password for ${u.name} and email it to ${u.email}?\n\nTheir current password will stop working immediately.`)) return;
    setBusyUserId(u.id);
    try {
      const { data } = await api.post(`/super-admin/tenants/${tenantId}/users/${u.id}/resend-welcome`);
      if (data.welcome_email_sent) {
        toast.success(`Welcome email re-sent to ${u.email}`);
      } else {
        toast.warning('Password reset but email could not be delivered. Use the displayed credentials.');
      }
      setResendCreds(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyUserId(null); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="tenant-drawer">
      <div className="flex-1 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <aside className="w-full sm:w-[640px] max-w-full bg-slate-50 border-l border-slate-200 shadow-2xl flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700 font-bold">Tenant configuration</div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mt-0.5 truncate">{tenant?.company_name || '—'}</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{tenantId}</code>
              {tenant?.company_domain && <> · {tenant.company_domain}</>}
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100" data-testid="drawer-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </header>

        {/* Tabs */}
        <nav className="bg-white border-b border-slate-200 px-6 flex items-center gap-1 overflow-x-auto" data-testid="drawer-tabs">
          {[
            { k: 'overview', l: 'Overview' },
            { k: 'config',   l: 'Configuration' },
            { k: 'users',    l: 'Users' },
            { k: 'branches', l: 'Branches' },
            { k: 'audit',    l: 'Audit' },
          ].map(t => (
            <button key={t.k} onClick={() => loadTab(t.k)}
                    data-testid={`drawer-tab-${t.k}`}
                    className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                      tab === t.k ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}>
              {t.l}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

          {!loading && tab === 'overview' && tenant && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Pill icon={Users}          label="Employees"   value={stats?.employees} testid="ov-employees" />
                <Pill icon={Boxes}          label="Assets"      value={stats?.assets} testid="ov-assets" />
                <Pill icon={Ticket}         label="Open tickets" value={stats?.tickets_open} sub={`of ${stats?.tickets_total ?? 0} total`} testid="ov-tickets" />
                <Pill icon={FolderArchive}  label="Vault files" value={stats?.vault_files} testid="ov-vault" />
                <Pill icon={Map}            label="Branches"    value={stats?.branches} testid="ov-branches" />
                <Pill icon={FolderTree}     label="Departments" value={stats?.departments} testid="ov-departments" />
                <Pill icon={ShieldCheck}    label="Admins"      value={stats?.admins} testid="ov-admins" />
                <Pill icon={BadgeCheck}     label="All users"   value={stats?.all_users} testid="ov-all-users" />
              </div>
              <SectionCard title="Tenant info">
                <RowKV k="Plan"      v={<span className="font-semibold uppercase">{tenant.subscription_plan || 'starter'}</span>} />
                <RowKV k="Status"    v={<StatusDot status={tenant.status} />} />
                <RowKV k="Subscription" v={
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tenant.subscription_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {tenant.subscription_active !== false ? 'Active' : 'Paused'}
                  </span>
                } />
                <RowKV k="Billing" v={
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tenant.billing_enabled ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {tenant.billing_enabled ? 'Enabled' : 'Off'}
                  </span>
                } />
                <RowKV k="Industry"  v={tenant.industry || '—'} />
                <RowKV k="Type"      v={tenant.company_type || '—'} />
                <RowKV k="Time zone" v={tenant.time_zone || '—'} />
                <RowKV k="Storage"   v={`${tenant.storage_allocation_gb ?? 0} GB allocated`} />
                <RowKV k="Created"   v={tenant.created_at?.slice(0, 10) || '—'} />
                {tenant.suspended_at && <RowKV k="Suspended" v={tenant.suspended_at.slice(0, 10)} />}
                {tenant.company_address && (
                  <RowKV k="Address" v={<span className="whitespace-pre-line">{tenant.company_address}</span>} />
                )}
              </SectionCard>
            </>
          )}

          {!loading && tab === 'config' && edit && (
            <div className="space-y-4">
              <SectionCard title="Identity">
                <FieldRow label="Company name *" testid="cfg-name">
                  <Input value={edit.company_name} onChange={(e) => setEdit({ ...edit, company_name: e.target.value })} className="rounded-lg" />
                </FieldRow>
                <FieldRow label="Primary domain" testid="cfg-domain">
                  <Input value={edit.company_domain} onChange={(e) => setEdit({ ...edit, company_domain: e.target.value })} className="rounded-lg" placeholder="acme.com" />
                </FieldRow>
                <FieldRow label="Industry" testid="cfg-industry">
                  <Input value={edit.industry} onChange={(e) => setEdit({ ...edit, industry: e.target.value })} className="rounded-lg" />
                </FieldRow>
                <FieldRow label="Company type" testid="cfg-type">
                  <Input value={edit.company_type} onChange={(e) => setEdit({ ...edit, company_type: e.target.value })} className="rounded-lg" placeholder="Private Limited" />
                </FieldRow>
                <FieldRow label="Address" testid="cfg-address">
                  <Textarea rows={3} value={edit.company_address} onChange={(e) => setEdit({ ...edit, company_address: e.target.value })} className="rounded-lg" />
                </FieldRow>
              </SectionCard>

              <SectionCard title="Plan & resources">
                <FieldRow label="Subscription plan" testid="cfg-plan">
                  <Select value={edit.subscription_plan} onValueChange={(v) => setEdit({ ...edit, subscription_plan: v })}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => (
                        <SelectItem key={p.v} value={p.v}>
                          <div className="flex flex-col">
                            <span className="font-semibold">{p.l}</span>
                            <span className="text-[10px] text-slate-500">{p.hint}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Storage allocation (GB)" testid="cfg-storage">
                  <Input type="number" min={0} value={edit.storage_allocation_gb}
                         onChange={(e) => setEdit({ ...edit, storage_allocation_gb: e.target.value })}
                         className="rounded-lg" />
                </FieldRow>
                <FieldRow label="Time zone" testid="cfg-tz">
                  <Select value={edit.time_zone} onValueChange={(v) => setEdit({ ...edit, time_zone: v })}>
                    <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </FieldRow>
              </SectionCard>

              <SectionCard title="Subscription & billing">
                <ToggleRow
                  label="Subscription active"
                  desc="Pause to immediately freeze the tenant. They can still log in but most actions are gated by app-level checks (Phase 2)."
                  on={tenant?.subscription_active !== false}
                  onClick={toggleSubscription}
                  disabled={saving}
                  testid="toggle-subscription"
                />
                <ToggleRow
                  label="Billing enabled"
                  desc="When ON, this tenant contributes to the MRR card on the platform dashboard. Doesn't enforce payment."
                  on={!!tenant?.billing_enabled}
                  onClick={toggleBilling}
                  disabled={saving}
                  testid="toggle-billing"
                  accent="amber"
                />
              </SectionCard>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2" data-testid="danger-zone">
                <div className="flex items-center gap-2 text-red-800 font-bold text-sm">
                  <AlertTriangle className="w-4 h-4" /> Danger zone
                </div>
                <p className="text-xs text-red-700">Hard delete removes this tenant and all its data forever. The audit trail of the deletion is preserved on the platform.</p>
                <Button onClick={hardDelete} disabled={saving} data-testid="hard-delete-tenant"
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Permanently delete tenant
                </Button>
              </div>

              <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="save-tenant-config"
                        className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          )}

          {!loading && tab === 'users' && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="tab-users-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  <tr>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-left py-2">Role</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-slate-400 italic">No users in this tenant.</td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-slate-900">{u.name}</div>
                        <div className="text-[11px] text-slate-500">{u.email}</div>
                      </td>
                      <td className="py-2 text-xs uppercase font-semibold text-slate-600">{u.role}</td>
                      <td className="py-2">
                        {u.terminated
                          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Terminated</span>
                          : u.active
                            ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{u.onboarding_status || 'active'}</span>
                            : <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">Inactive</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm" variant="ghost"
                          disabled={busyUserId === u.id || u.terminated}
                          onClick={() => resendWelcome(u)}
                          data-testid={`resend-welcome-${u.id}`}
                          title="Reset password & re-email credentials"
                          className="h-7 px-2 text-[#3D4A8E] hover:bg-indigo-50">
                          {busyUserId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'branches' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="tab-branches">
              {branches.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No branches configured yet.</div>
              ) : (
                <ul className="space-y-2">
                  {branches.map(b => (
                    <li key={b.id} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                      <Map className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{b.name}</div>
                        <div className="text-xs text-slate-500">{[b.city, b.state, b.country].filter(Boolean).join(' · ') || '—'}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {!loading && tab === 'audit' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2.5" data-testid="tab-audit">
              {audit.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No audit entries for this tenant yet.</div>
              ) : audit.map((a, i) => (
                <div key={i} className="flex gap-2.5 text-xs border-b border-slate-100 last:border-0 pb-2.5">
                  <span className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                    a.severity === 'error' ? 'bg-red-500' : a.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-700">{(a.action || '').replace(/_/g, ' ')}</div>
                    <div className="text-[10px] text-slate-500">{a.ts?.replace('T', ' ').slice(0, 19)} {a.actor_name && <> · by {a.actor_name}</>}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* Resend-welcome backup credentials dialog */}
      {resendCreds && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setResendCreds(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="resend-creds-dialog">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound className="w-5 h-5 text-amber-700" />
              <h3 className="font-heading text-xl font-bold text-slate-900">Welcome email re-sent</h3>
            </div>
            <div className={`rounded-xl p-3 text-xs flex items-start gap-2 ${resendCreds.welcome_email_sent ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-amber-50 border border-amber-200 text-amber-900'}`}>
              {resendCreds.welcome_email_sent ? <BadgeCheck className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
              <div>
                {resendCreds.welcome_email_sent
                  ? <>A fresh password was emailed to <b>{resendCreds.email}</b>. The credentials below are a backup copy.</>
                  : <>Email delivery failed — share these credentials manually. The password has already been reset.</>}
              </div>
            </div>
            <div className="mt-3 space-y-2 text-xs">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Email</div>
                <code className="font-mono text-slate-900">{resendCreds.email}</code>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Temporary password</div>
                <code className="font-mono text-slate-900 break-all">{resendCreds.temp_password}</code>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => {
                navigator.clipboard.writeText(`${resendCreds.email} / ${resendCreds.temp_password}`);
                toast.success('Copied');
              }} className="flex-1 rounded-full">Copy both</Button>
              <Button onClick={() => setResendCreds(null)} className="flex-1 rounded-full bg-[#3D4A8E] text-white" data-testid="resend-creds-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pill({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-heading text-xl font-bold text-slate-900 mt-1 tabular-nums">{value ?? '—'}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{title}</div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function RowKV({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 text-sm">
      <div className="text-slate-500 font-medium">{k}</div>
      <div className="text-slate-900 text-right max-w-[60%]">{v}</div>
    </div>
  );
}

function FieldRow({ label, testid, children }) {
  return (
    <div className="py-2" data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    active:    { l: 'Active',    c: 'bg-emerald-500' },
    suspended: { l: 'Suspended', c: 'bg-orange-500' },
    deleted:   { l: 'Deleted',   c: 'bg-red-500' },
  }[status] || { l: status, c: 'bg-slate-400' };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${map.c}`} />
      <span className="font-semibold">{map.l}</span>
    </span>
  );
}

function ToggleRow({ label, desc, on, onClick, disabled, testid, accent }) {
  const colour = on
    ? (accent === 'amber' ? 'bg-amber-500' : 'bg-emerald-500')
    : 'bg-slate-300';
  return (
    <div className="flex items-start justify-between gap-4 py-2.5" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {desc && <div className="text-xs text-slate-500 mt-0.5">{desc}</div>}
      </div>
      <button onClick={onClick} disabled={disabled} data-testid={`${testid}-btn`}
              className={`relative w-11 h-6 rounded-full transition-colors ${colour} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <span className={`absolute top-0.5 ${on ? 'right-0.5' : 'left-0.5'} w-5 h-5 rounded-full bg-white shadow transition-transform`} />
      </button>
    </div>
  );
}
